// src/services/cloudflareStreamService.js
class CloudflareStreamService {
  constructor() {
    this.accountId = process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID;
    this.apiToken = process.env.REACT_APP_CLOUDFLARE_API_TOKEN;
    this.streamDomain = process.env.REACT_APP_CLOUDFLARE_STREAM_DOMAIN || "customer-apz9t94a2fzrv1a9.cloudflarestream.com";
    this.apiBaseUrl = process.env.REACT_APP_API_URL || "https://elderfit.onrender.com/api";
  }

  async getSignedVideoUrl(videoId, tier = "basic") {
    try {
      const token = localStorage.getItem("token");
      console.log("Getting signed video URL for:", { videoId, tier, apiBaseUrl: this.apiBaseUrl });
      
      const response = await fetch(
        `${this.apiBaseUrl}/cloudflare-stream/get-video-url?videoId=${videoId}&tier=${tier}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied: Upgrade required");
        }
        throw new Error(`Failed to get video URL: ${response.status}`);
      }

      const data = await response.json();
      console.log("Received signed URL from backend:", data.url);
      return data.url;
    } catch (error) {
      console.error("Error getting signed video URL:", error);
      throw error;
    }
  }

  getUnsignedPlayerUrl(videoId) {
    const domain = String(this.streamDomain || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `https://${domain}/${videoId}/iframe`;
  }

  isLocalDev() {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    return host === "localhost" || host === "127.0.0.1";
  }

  async getPlayerUrl(videoId, tier = "basic", options = {}) {
    try {
      console.log("Getting player URL for:", { videoId, tier, purpose: options.purpose });
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || this.apiBaseUrl;
      const purpose = options.purpose ? `&purpose=${encodeURIComponent(options.purpose)}` : "";
      const response = await fetch(
        `${apiBaseUrl}/cloudflare-stream/get-video-url?videoId=${videoId}&tier=${tier}${purpose}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (!response.ok) {
        let details = response.statusText;
        try {
          const errBody = await response.json();
          details = errBody.error || errBody.message || details;
          if (Array.isArray(errBody.missing) && errBody.missing.length) {
            details = `${details} (missing ${errBody.missing.join(", ")})`;
          }
        } catch {
          // ignore JSON parse errors and use status text
        }

        if (response.status === 403) {
          throw new Error("Access denied: Upgrade required");
        }

        // 503 means the unsigned player URL itself is blocked (signed URLs required).
        if (
          this.isLocalDev() &&
          options.allowUnsignedFallback !== false &&
          response.status !== 503
        ) {
          console.warn("Video URL API failed locally; using unsigned Cloudflare player URL.", details);
          return this.getUnsignedPlayerUrl(videoId);
        }

        throw new Error(`Failed to generate secure URL: ${details}`);
      }
      const data = await response.json();
      console.log("Generated video URL:", data.url);
      return data.url;
    } catch (error) {
      console.error("Error getting player URL:", error);
      throw error;
    }
  }

  hasAccess(userTier, requiredTier) {
    const tierLevels = { basic: 0, premium: 1, elite: 2 };
    const userLevel = tierLevels[userTier] || 0;
    const requiredLevel = tierLevels[requiredTier] || 0;
    return userLevel >= requiredLevel;
  }

  async uploadVideo(file, metadata = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("metadata", JSON.stringify(metadata));

      const response = await fetch(`${this.apiBaseUrl}/cloudflare-stream/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error uploading video:", error);
      throw error;
    }
  }

  async getVideoDetails(videoId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/cloudflare-stream/video/${videoId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get video details: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting video details:", error);
      throw error;
    }
  }
}

const cloudflareStreamService = new CloudflareStreamService();
export default cloudflareStreamService;
